import React, { useMemo, forwardRef } from 'react';
import DOMPurify, { Config } from 'dompurify';

type SafeHTMLProps<T extends React.ElementType = 'div'> = {
	html: string;
	config?: Config;
	component?: T; // HTML tags OR React components
	className?: string;
} & Omit<React.ComponentProps<T>, 'as' | 'dangerouslySetInnerHTML' | 'children'>;

function SafeHTMLInner<T extends React.ElementType = 'div'>(
	{ html, config, component, className, ...rest }: SafeHTMLProps<T>,
	ref: React.ForwardedRef<HTMLElement>
) {
	const Tag = component || 'div';

	const clean = useMemo(() => {
		// always force sanitize result to string
		return DOMPurify.sanitize(html, { RETURN_DOM: false, ...config });
	}, [html, config]);

	return (
		<Tag {...rest} ref={ref} className={className} dangerouslySetInnerHTML={{ __html: clean }} />
	);
}

const SafeHTML = forwardRef(SafeHTMLInner) as <T extends React.ElementType = 'div'>(
	props: SafeHTMLProps<T> & { ref?: React.ForwardedRef<HTMLElement> }
) => React.ReactElement | null;

export { SafeHTML };
